import React, { Component } from "react";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import styled from "styled-components";

import { withFormik, Form, Field } from "formik";
import * as Yup from "yup";

import ActionFormButton from "../../../common/ActionFormButton";
import ChangePassword from "./components/ChangePassword";
import AuthorShopSection from "./AuthorShopSection";

import {
  handleRepeatedFormValues,
  renderRowWithTwoItems,
  renderSectionTitle,
  getRowItemObject,
  Section,
  Wrapper,
  renderRowWithSingleItem,
  renderRowWithSingleCheckbox,
} from "../../../common/FormUtils";
import Roles from "../../../../types/Roles";
import { Author, KnowledgeSource } from "../../../../types/Author";
import ImageUpload from "../../../common/ImageUpload";
import {
  createCommunityAgent,
  createInstantVoiceClone,
} from "../../onboarding/OnboardingAPI";
import {
  ingestKnowledgeSource,
  listKnowledgeSources,
  deleteKnowledgeSource,
} from "../../../../services/authorKnowledge";
import { Community } from "../../../../types/Community";

const KNOWLEDGE_ACCEPT = ".pdf,.txt,.md,.doc,.docx,.epub";

const EditPasswordButtonWrapper = styled.div`
  display: block;
`;

const AUDIO_SAMPLE_EXTENSION_REGEX = /\.(mp3|wav|m4a|aac|ogg|flac|webm)$/i;

const InlineActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 12px 0 16px;
`;

const HelperText = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 12px;
`;

const StatusText = styled.div<{ $error?: boolean }>`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ $error }) => ($error ? "#d32f2f" : "rgba(0, 0, 0, 0.75)")};
  margin-top: 8px;
`;

const AudioPreview = styled.audio`
  width: 100%;
  margin-top: 12px;
`;

const KnowledgeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const KnowledgeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
`;

const KnowledgeMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const KnowledgeTags = styled.div`
  display: flex;
  gap: 6px;

  span {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(0, 0, 0, 0.6);
    background: rgba(0, 0, 0, 0.06);
    border-radius: 4px;
    padding: 2px 6px;
  }
`;

type Props = {
  setFieldValue: Function;
  onChageFormToEditMode: Function;
  onEditPassword: Function;
  handleSubmit: Function;
  onRemoveItem: Function;
  isSubmitting: boolean;
  item: any;
  mode: string;
  errors: any;
  values: any;
  communityId?: string;
  community?: Community | null;
};

type State = {
  isPasswordDialogOpen: boolean;
  voiceSampleFile: File | null;
  voiceSamplePreviewUrl: string;
  isCreatingVoiceClone: boolean;
  isCreatingAgent: boolean;
  voiceAutomationMessage: string;
  voiceAutomationError: string;
  knowledgeSources: KnowledgeSource[];
  knowledgeFile: File | null;
  isLoadingKnowledge: boolean;
  isUploadingKnowledge: boolean;
  deletingSourceId: string | null;
  knowledgeMessage: string;
  knowledgeError: string;
};

class UserForm extends Component<Props, State> {
  state: State = {
    isPasswordDialogOpen: false,
    voiceSampleFile: null,
    voiceSamplePreviewUrl: "",
    isCreatingVoiceClone: false,
    isCreatingAgent: false,
    voiceAutomationMessage: "",
    voiceAutomationError: "",
    knowledgeSources: [],
    knowledgeFile: null,
    isLoadingKnowledge: false,
    isUploadingKnowledge: false,
    deletingSourceId: null,
    knowledgeMessage: "",
    knowledgeError: "",
  };

  componentDidMount() {
    this.refreshKnowledgeSources();
  }

  componentWillUnmount() {
    this.revokeVoiceSamplePreview();
  }

  refreshKnowledgeSources = async (): Promise<void> => {
    const authorId = this.props.values?.id;
    if (!authorId) return;
    this.setState({ isLoadingKnowledge: true });
    try {
      const sources = await listKnowledgeSources(authorId);
      this.setState({ knowledgeSources: sources });
    } catch (error) {
      console.error("Failed to load knowledge sources:", error);
    } finally {
      this.setState({ isLoadingKnowledge: false });
    }
  };

  handleKnowledgeFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.currentTarget.files?.[0] || null;
    if ((event.currentTarget as HTMLInputElement).value) {
      event.currentTarget.value = "";
    }
    if (!file) return;
    this.setState({
      knowledgeFile: file,
      knowledgeError: "",
      knowledgeMessage: `Ready to index ${file.name}.`,
    });
  };

  handleIngestKnowledge = async (): Promise<void> => {
    const { values } = this.props;
    const { knowledgeFile } = this.state;

    if (!values?.id) {
      this.setState({
        knowledgeError: "Save the author first, then add knowledge sources.",
        knowledgeMessage: "",
      });
      return;
    }
    if (!knowledgeFile) {
      this.setState({
        knowledgeError: "Pick a file to index.",
        knowledgeMessage: "",
      });
      return;
    }

    this.setState({
      isUploadingKnowledge: true,
      knowledgeError: "",
      knowledgeMessage: "Indexing… large files can take a minute.",
    });

    try {
      const result = await ingestKnowledgeSource({
        author: values as Author,
        file: knowledgeFile,
      });
      const voiceNote = !values.agentId
        ? " No voice agent yet, so this powers text chat only."
        : result.voiceAttached
        ? " Voice agent knowledge base updated."
        : ` Text chat is ready, but the voice knowledge base failed: ${
            result.voiceError || "unknown error"
          }`;
      this.setState({
        knowledgeFile: null,
        knowledgeMessage: `Indexed ${knowledgeFile.name}.${voiceNote}`,
      });
      await this.refreshKnowledgeSources();
    } catch (error: any) {
      this.setState({
        knowledgeError:
          error?.message || "We couldn't index that file. Please try again.",
        knowledgeMessage: "",
      });
    } finally {
      this.setState({ isUploadingKnowledge: false });
    }
  };

  handleDeleteKnowledgeSource = async (
    source: KnowledgeSource
  ): Promise<void> => {
    const { values } = this.props;
    if (!values?.id) return;
    this.setState({ deletingSourceId: source.id, knowledgeError: "" });
    try {
      await deleteKnowledgeSource({ author: values as Author, source });
      await this.refreshKnowledgeSources();
      this.setState({ knowledgeMessage: `Removed ${source.title}.` });
    } catch (error: any) {
      this.setState({
        knowledgeError:
          error?.message || "We couldn't remove that source. Please try again.",
      });
    } finally {
      this.setState({ deletingSourceId: null });
    }
  };

  renderKnowledgeBaseSection = (): JSX.Element => {
    const { mode, values } = this.props;
    const {
      knowledgeSources,
      knowledgeFile,
      isLoadingKnowledge,
      isUploadingKnowledge,
      deletingSourceId,
      knowledgeMessage,
      knowledgeError,
    } = this.state;

    const savedAuthor = !!values?.id;
    const busy = isUploadingKnowledge;

    return (
      <Section>
        {renderSectionTitle("Knowledge Base")}
        <HelperText>
          Upload the author's own material (books, transcripts, articles) so the
          AI answers grounded in their work. Text chat retrieves the most
          relevant passages per question. If the author has a voice agent, the
          same document is attached to the ElevenLabs knowledge base with RAG.
          Supported: PDF, TXT, MD, DOC, DOCX, EPUB.
        </HelperText>

        {!savedAuthor && (
          <StatusText>
            Save the author first, then upload knowledge sources here.
          </StatusText>
        )}

        <input
          accept={KNOWLEDGE_ACCEPT}
          style={{ display: "none" }}
          id="author-knowledge-file"
          type="file"
          disabled={mode === "detail" || busy || !savedAuthor}
          onChange={this.handleKnowledgeFileSelect}
        />
        <InlineActions>
          <label htmlFor="author-knowledge-file">
            <Button
              component="span"
              variant="outlined"
              color="default"
              disabled={mode === "detail" || busy || !savedAuthor}
            >
              {knowledgeFile ? "Replace File" : "Select File"}
            </Button>
          </label>
          <Button
            type="button"
            variant="contained"
            color="primary"
            disabled={mode === "detail" || busy || !savedAuthor || !knowledgeFile}
            onClick={this.handleIngestKnowledge}
          >
            {isUploadingKnowledge ? (
              <>
                <CircularProgress size={16} style={{ marginRight: 8 }} />
                Indexing
              </>
            ) : (
              "Index Source"
            )}
          </Button>
        </InlineActions>

        {knowledgeFile && (
          <HelperText>
            Selected: <strong>{knowledgeFile.name}</strong>
          </HelperText>
        )}

        {isLoadingKnowledge ? (
          <StatusText>Loading sources…</StatusText>
        ) : knowledgeSources.length > 0 ? (
          <KnowledgeList>
            {knowledgeSources.map((source) => (
              <KnowledgeRow key={source.id}>
                <KnowledgeMeta>
                  <strong>{source.title}</strong>
                  <KnowledgeTags>
                    <span>{source.status}</span>
                    {source.elevenLabsDocId ? <span>voice</span> : null}
                  </KnowledgeTags>
                </KnowledgeMeta>
                <Button
                  type="button"
                  size="small"
                  color="secondary"
                  disabled={mode === "detail" || deletingSourceId === source.id}
                  onClick={() => this.handleDeleteKnowledgeSource(source)}
                >
                  {deletingSourceId === source.id ? "Removing…" : "Remove"}
                </Button>
              </KnowledgeRow>
            ))}
          </KnowledgeList>
        ) : (
          savedAuthor && <StatusText>No sources indexed yet.</StatusText>
        )}

        {knowledgeMessage && <StatusText>{knowledgeMessage}</StatusText>}
        {knowledgeError && <StatusText $error>{knowledgeError}</StatusText>}
      </Section>
    );
  };

  revokeVoiceSamplePreview = () => {
    const previewUrl = this.state.voiceSamplePreviewUrl;
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  handleVoiceSampleUpload = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.currentTarget.files?.[0] || null;

    if ((event.currentTarget as HTMLInputElement).value) {
      event.currentTarget.value = "";
    }

    if (!file) return;

    if ((file.type || "").startsWith("video/")) {
      this.setState({
        voiceAutomationError:
          "That looks like a video file. Upload an audio-only sample such as MP3, WAV, M4A, OGG, FLAC, or audio WebM.",
        voiceAutomationMessage: "",
      });
      return;
    }

    const isAudioFile =
      (file.type || "").startsWith("audio/") ||
      AUDIO_SAMPLE_EXTENSION_REGEX.test(file.name);

    if (!isAudioFile || file.size <= 0) {
      this.setState({
        voiceAutomationError:
          "Please upload a valid non-empty audio sample such as MP3, WAV, M4A, OGG, FLAC, or audio WebM.",
        voiceAutomationMessage: "",
      });
      return;
    }

    this.revokeVoiceSamplePreview();
    const previewUrl = URL.createObjectURL(file);
    this.setState({
      voiceSampleFile: file,
      voiceSamplePreviewUrl: previewUrl,
      voiceAutomationError: "",
      voiceAutomationMessage:
        "Audio sample ready. Create the voice clone, then save the author to persist the new IDs.",
    });
  };

  handleCreateVoiceClone = async (): Promise<void> => {
    const { voiceSampleFile } = this.state;
    const { values, setFieldValue, community } = this.props;

    if (!voiceSampleFile) {
      this.setState({
        voiceAutomationError:
          "Upload an audio sample before creating the voice clone.",
        voiceAutomationMessage: "",
      });
      return;
    }

    this.setState({
      isCreatingVoiceClone: true,
      voiceAutomationError: "",
      voiceAutomationMessage: "",
    });

    try {
      const cloneResult = await createInstantVoiceClone({
        file: voiceSampleFile,
        name: `${(values.fullName || community?.title || "Author").trim()} Voice`,
        description: community?.title
          ? `Voice clone for ${values.fullName || "author"} in ${community.title}.`
          : `Voice clone for ${values.fullName || "author"}.`,
      });

      setFieldValue("voiceId", cloneResult.voiceId);
      this.setState({
        voiceAutomationMessage:
          "Voice clone created. Save the author form to persist the new voice ID.",
      });
    } catch (error: any) {
      this.setState({
        voiceAutomationError:
          error?.message ||
          "We couldn't create the voice clone. Please try again with a shorter, cleaner audio sample.",
      });
    } finally {
      this.setState({ isCreatingVoiceClone: false });
    }
  };

  handleCreateAgent = async (): Promise<void> => {
    const { values, setFieldValue, community, communityId } = this.props;
    const voiceId = (values.voiceId || "").trim();

    if (!voiceId) {
      this.setState({
        voiceAutomationError:
          "Create or paste a voice ID before creating the ElevenLabs agent.",
        voiceAutomationMessage: "",
      });
      return;
    }

    this.setState({
      isCreatingAgent: true,
      voiceAutomationError: "",
      voiceAutomationMessage: "",
    });

    try {
      const communityName =
        community?.title?.trim() || values.fullName?.trim() || communityId || "Community";
      const normalizedTags = Array.isArray(community?.tags)
        ? community.tags
        : typeof values.tags === "string"
        ? values.tags.split(",").map((tag) => tag.trim()).filter(Boolean)
        : Array.isArray(values.tags)
        ? values.tags
        : [];

      const agentResult = await createCommunityAgent({
        communityName,
        communityDescription: community?.description || "",
        categoryName: community?.categoryName || "",
        tags: normalizedTags,
        writingStyle: community?.writingStyle || "",
        authorName: values.fullName || community?.author?.fullName || "",
        authorDescription: values.description || "",
        communityAiGuidelines: "",
        authorAiGuidelines: values.aiGuidelines || "",
        voiceId,
        language: community?.lang,
      });

      setFieldValue("agentId", agentResult.agentId);
      this.setState({
        voiceAutomationMessage:
          "Agent created. Save the author form to persist the new agent ID.",
      });
    } catch (error: any) {
      this.setState({
        voiceAutomationError:
          error?.message ||
          "We couldn't create the ElevenLabs agent. Please check the voice ID and try again.",
      });
    } finally {
      this.setState({ isCreatingAgent: false });
    }
  };

  renderVoiceAutomationSection = (): JSX.Element => {
    const {
      voiceSampleFile,
      voiceSamplePreviewUrl,
      isCreatingVoiceClone,
      isCreatingAgent,
      voiceAutomationMessage,
      voiceAutomationError,
    } = this.state;
    const { mode, values } = this.props;

    const busy = isCreatingVoiceClone || isCreatingAgent;

    return (
      <Section>
        {renderSectionTitle("Voice & Agent")}
        <HelperText>
          Upload an audio sample here if you want to create the ElevenLabs voice
          clone and agent manually from the author form. The generated IDs will
          fill the <code>voiceId</code> and <code>agentId</code> fields, then you
          can save the author to persist them.
        </HelperText>

        <input
          accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac,.webm"
          style={{ display: "none" }}
          id="author-voice-sample"
          type="file"
          disabled={mode === "detail" || busy}
          onChange={this.handleVoiceSampleUpload}
        />
        <InlineActions>
          <label htmlFor="author-voice-sample">
            <Button
              component="span"
              variant="outlined"
              color="default"
              disabled={mode === "detail" || busy}
            >
              {voiceSampleFile ? "Replace Audio Sample" : "Upload Audio Sample"}
            </Button>
          </label>
          <Button
            type="button"
            variant="contained"
            color="primary"
            disabled={mode === "detail" || busy || !voiceSampleFile}
            onClick={this.handleCreateVoiceClone}
          >
            {isCreatingVoiceClone ? (
              <>
                <CircularProgress size={16} style={{ marginRight: 8 }} />
                Creating Voice Clone
              </>
            ) : (
              "Create Voice Clone"
            )}
          </Button>
          <Button
            type="button"
            variant="contained"
            color="primary"
            disabled={mode === "detail" || busy || !(values.voiceId || "").trim()}
            onClick={this.handleCreateAgent}
          >
            {isCreatingAgent ? (
              <>
                <CircularProgress size={16} style={{ marginRight: 8 }} />
                Creating Agent
              </>
            ) : (
              "Create Agent"
            )}
          </Button>
        </InlineActions>

        {voiceSampleFile && (
          <HelperText>
            Sample selected: <strong>{voiceSampleFile.name}</strong>
          </HelperText>
        )}
        {voiceSamplePreviewUrl && (
          <AudioPreview controls src={voiceSamplePreviewUrl} />
        )}
        {voiceAutomationMessage && (
          <StatusText>{voiceAutomationMessage}</StatusText>
        )}
        {voiceAutomationError && (
          <StatusText $error>{voiceAutomationError}</StatusText>
        )}
      </Section>
    );
  };

  onTogglePassowordDialog = (): void => {
    const { isPasswordDialogOpen } = this.state;

    this.setState({
      isPasswordDialogOpen: !isPasswordDialogOpen,
    });
  };

  onChangePassword = (newPassword: string): void => {
    const { onEditPassword } = this.props;

    this.setState(
      {
        isPasswordDialogOpen: false,
      },
      () => onEditPassword(newPassword)
    );
  };

  renderPasswordSection = (): Object => {
    const passwordConfirmInputFieldData = getRowItemObject(
      "Repeat the Password",
      "Enter the Password again.",
      "password",
      "passwordConfirm"
    );
    const passwordInputFieldData = getRowItemObject(
      "Password",
      "Enter the Password",
      "password",
      "password"
    );

    return (
      <Section>
        {renderSectionTitle("Password")}
        {renderRowWithTwoItems(
          passwordInputFieldData,
          passwordConfirmInputFieldData,
          this.props
        )}
      </Section>
    );
  };

  renderEditPasswordButton = (): Object => (
    <EditPasswordButtonWrapper>
      <Button
        onClick={() => this.onTogglePassowordDialog()}
        disableRipple
        color="primary"
        variant="contained"
        size="large"
      >
        Edit Password
      </Button>
    </EditPasswordButtonWrapper>
  );
  renderImageSection = (): any => (
    <Section>
      {renderSectionTitle("Profile")}
      {this.renderImageField()}
      {renderSectionTitle("Cover")}
      {this.renderCoverURL()}
    </Section>
  );
  renderCoverURL = () => {
    const imageInputFieldData = getRowItemObject(
      "",
      "Selecciona una imagen para mostrar",
      "file",
      "coverURL"
    );

    return (
      <Field name="coverURL">
        {({ field, form }) => (
          <ImageUpload
            field={field}
            form={form}
            actionId={this.props.values?.id}
            reference={"Author"}
          />
        )}
      </Field>
    );
  };
  renderImageField = () => {
    const imageInputFieldData = getRowItemObject(
      "",
      "Selecciona una imagen para mostrar",
      "file",
      "profilePhotoURL"
    );

    return (
      <Field name="profilePhotoURL">
        {({ field, form }) => (
          <ImageUpload
            field={field}
            form={form}
            actionId={this.props.values?.id}
            reference={"Author"}
          />
        )}
      </Field>
    );
  };
  renderUserInfoSection = () => {
    const nameInputFieldData = getRowItemObject(
      "Nombre",
      "Ingresa el nombre",
      "text",
      "fullName"
    );
    const tagsInputFieldData = getRowItemObject(
      "Tags",
      "Ingresa las tags",
      "text",
      "tags"
    );
    const shortDescriptionInputFieldData = getRowItemObject(
      "Short Description",
      "Ingresa la  short description",
      "text",
      "shortDescription"
    );
    const descriptionInputFieldData = getRowItemObject(
      "Description",
      "Ingresa la description",
      "text",
      "description"
    );
    const welcomingMessageInputFieldData = getRowItemObject(
      "Welcoming Message",
      "Type the welcoming message",
      "text",
      "welcomingMessage"
    );
    const positionInputFieldData = getRowItemObject(
      "Position",
      "Ingresa la posición",
      "number",
      "position"
    );
    const aiGuidelinesInputFieldData = getRowItemObject(
      "AI Guidelines",
      "Type AI guidelines for this author",
      "textarea",
      "aiGuidelines"
    );

    const bookingURLInputFieldData = getRowItemObject(
      "Booking URL",
      "Type the booking URL",
      "text",
      "bookingURL"
    );

    const voiceIdInputFieldData = getRowItemObject(
      "Voice Id",
      "Type the voice Id",
      "text",
      "voiceId"
    );
    const agentIdInputFieldData = getRowItemObject(
      "Agent Id",
      "Type the agent Id",
      "text",
      "agentId"
    );
    return (
      <Section>
        {renderRowWithTwoItems(
          nameInputFieldData,
          tagsInputFieldData,
          this.props
        )}
        {renderRowWithTwoItems(
          shortDescriptionInputFieldData,
          descriptionInputFieldData,
          this.props
        )}
        {renderRowWithTwoItems(
          welcomingMessageInputFieldData,
          bookingURLInputFieldData,
          this.props
        )}
        {renderRowWithTwoItems(voiceIdInputFieldData, agentIdInputFieldData, {
          ...this.props,
        })}
        {renderRowWithSingleItem(positionInputFieldData, this.props)}
        {renderRowWithSingleItem(aiGuidelinesInputFieldData, this.props)}
      </Section>
    );
  };

  renderLinksAndShopSection = () => {
    const websiteInputFieldData = getRowItemObject(
      "Website",
      "https://...",
      "text",
      "website"
    );
    const instagramInputFieldData = getRowItemObject(
      "Instagram",
      "https://instagram.com/...",
      "text",
      "socials.instagram"
    );
    const tiktokInputFieldData = getRowItemObject(
      "TikTok",
      "https://tiktok.com/@...",
      "text",
      "socials.tiktok"
    );
    const youtubeInputFieldData = getRowItemObject(
      "YouTube",
      "https://youtube.com/@...",
      "text",
      "socials.youtube"
    );
    const xInputFieldData = getRowItemObject(
      "X (Twitter)",
      "https://x.com/...",
      "text",
      "socials.x"
    );
    const facebookInputFieldData = getRowItemObject(
      "Facebook",
      "https://facebook.com/...",
      "text",
      "socials.facebook"
    );
    const linkedinInputFieldData = getRowItemObject(
      "LinkedIn",
      "https://linkedin.com/in/...",
      "text",
      "socials.linkedin"
    );
    const whatsappInputFieldData = getRowItemObject(
      "WhatsApp",
      "https://wa.me/...",
      "text",
      "socials.whatsapp"
    );
    const spotifyInputFieldData = getRowItemObject(
      "Spotify",
      "https://open.spotify.com/...",
      "text",
      "socials.spotify"
    );
    const shopEnabledFieldData = getRowItemObject(
      "Shop enabled",
      "Show a shop on this author's profile (products are managed via the tribed-admin tools)",
      "checkbox",
      "shopEnabled"
    );

    return (
      <Section>
        {renderSectionTitle("Links & Social")}
        {renderRowWithSingleItem(websiteInputFieldData, this.props)}
        {renderRowWithTwoItems(
          instagramInputFieldData,
          tiktokInputFieldData,
          this.props
        )}
        {renderRowWithTwoItems(
          youtubeInputFieldData,
          xInputFieldData,
          this.props
        )}
        {renderRowWithTwoItems(
          facebookInputFieldData,
          linkedinInputFieldData,
          this.props
        )}
        {renderRowWithTwoItems(
          whatsappInputFieldData,
          spotifyInputFieldData,
          this.props
        )}
        {renderSectionTitle("Shop")}
        {renderRowWithSingleCheckbox(shopEnabledFieldData, this.props)}
      </Section>
    );
  };

  render() {
    const {
      onChageFormToEditMode,
      onRemoveItem,
      handleSubmit,
      isSubmitting,
      item,
      mode,
    } = this.props;

    const { isPasswordDialogOpen } = this.state;

    return (
      <Wrapper>
        <Form>
          {this.renderUserInfoSection()}
          {this.renderLinksAndShopSection()}
          <AuthorShopSection
            authorId={this.props.values?.id}
            mode={this.props.mode}
          />
          {this.renderVoiceAutomationSection()}
          {this.renderKnowledgeBaseSection()}
          {this.renderImageSection()}
          <ActionFormButton
            onChageFormToEditMode={onChageFormToEditMode}
            onClick={this.props.handleSubmit}
            onRemoveItem={onRemoveItem}
            disabled={
              isSubmitting ||
              this.state.isCreatingVoiceClone ||
              this.state.isCreatingAgent
            }
            entity="Author"
            canBeRemoved
            mode={mode}
          ></ActionFormButton>
        </Form>
      </Wrapper>
    );
  }
}

const getPasswordValidation = (mode: string): Object => {
  if (mode === "create") {
    return {};
  }

  return {};
};

const CustomForm = withFormik({
  mapPropsToValues: ({
    item,
    communityId,
  }: {
    item: Author;
    onCreateItem;
    onEditItem;
    mode;
    communityId?: string;
  }) => ({
    id: item.id || "",
    fullName: item.fullName || "",
    profilePhotoURL: item.profilePhotoURL || "",
    coverURL: item.coverURL || "",
    description: item.description || "",
    shortDescription: item.shortDescription || "",
    tags: item.tags || [],
    welcomingMessage: item.welcomingMessage || "",
    createdAt: item.createdAt || "",
    voiceId: item?.voiceId || "",
    bookingURL: item?.bookingURL || "",
    agentId: item?.agentId || "",
    aiGuidelines: item?.aiGuidelines || "",
    website: item?.website || "",
    socials: item?.socials || {},
    shopEnabled: item?.shopEnabled || false,
    communityId: item?.communityId || communityId || "",
    position:
      typeof item?.position === "number"
        ? item.position
        : item?.position
        ? Number(item.position)
        : "",
  }),

  validationSchema: ({ item, usernames, mode }) =>
    Yup.lazy(() => Yup.object().shape({})),

  handleSubmit(values, { setSubmitting, props }) {
    const { onCreateItem, onEditItem, mode } = props;

    const properCallback = mode === "edit" ? onEditItem : onCreateItem;

    properCallback({
      ...values,
      position:
        typeof values.position === "number"
          ? values.position
          : Number(values.position) || 0,
    });

    setSubmitting(false);
  },

  //@ts-ignore
})(UserForm);

export default CustomForm;
